import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, InputText, Modal } from '@teo/components';
import { useGet } from '@/query/fetchHooks';
import { axios } from '@/axios';
import { config } from '@/config';

const T_PREFIX = 'pages.organisation_licensing_and_billing';
function OrganisationStatusToggle({ auth, className = '' }: any) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const authUser: any = auth?.user;
  const organisationId = authUser?.activeOrganisationId;
  const canChangeStatus = (authUser?.role || 0) >= 95;

  const [disabledOrganization, setDisabledOrganization] = useState<
    boolean | null
  >(null);
  const [nrOfMembers, setNrOfMembers] = useState(0);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmValue, setConfirmValue] = useState('');

  useGet(`/organisation/${organisationId}`, {
    enabled: canChangeStatus && !!organisationId,
    onSuccess: (result: any) => {
      setDisabledOrganization(!!result?.data?.disabledOrganization);
      setNrOfMembers(result?.data?.nrOfMembers || 0);
    },
  });

  const [nrToAnonymise, setNrToAnonymise] = useState<number | null>(null);
  useGet(`/organisationstatus/${organisationId}/impact`, {
    enabled: isConfirmOpen && disabledOrganization === false,
    onSuccess: (result: any) => {
      setNrToAnonymise(result?.data?.nrToAnonymise ?? 0);
    },
  });

  const isConfirmValid = confirmValue.trim() === String(nrToAnonymise);

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    setConfirmValue('');
  };

  const disableOrganisation = async () => {
    const result = await axios.put(
      `${config.backend}/organisation/${organisationId}`,
      { disabledOrganization: true }
    );
    setDisabledOrganization(!!result?.data?.disabledOrganization);

    void queryClient.invalidateQueries({ queryKey: ['/organisationdetails'] });
    void queryClient.invalidateQueries({ queryKey: ['/memberorganisations'] });
  };

  const onConfirm = async () => {
    if (!isConfirmValid) return;
    closeConfirm();
    await disableOrganisation();
  };

  if (!canChangeStatus || disabledOrganization === null) return null;

  return (
    <div className={className}>
      <Button
        variant="destruct"
        disabled={disabledOrganization}
        onClick={() => {
          setIsConfirmOpen(true);
        }}
      >
        {t(`${T_PREFIX}.disable_organisation`)}
      </Button>

      {isConfirmOpen && (
        <Modal isOpen>
          <Modal.Header onClose={closeConfirm}>
            {t(`${T_PREFIX}.disable_organisation_title`)}
          </Modal.Header>
          <Modal.Body>
            <h3 className="text-lg">
              {t(`${T_PREFIX}.disable_organisation_description`)}
            </h3>
            <p className="mt-3 text-base text-grey-08">
              {t(`${T_PREFIX}.disable_organisation_users`, {
                count: nrToAnonymise ? nrToAnonymise : 0,
              })}
            </p>
            {!!nrToAnonymise && (
              <div className="mt-3 rounded-lg bg-error-01 p-4">
                <p className="text-sm text-error-05">
                  {t(`${T_PREFIX}.disable_organisation_anonymise`, {
                    count: nrToAnonymise,
                  })}
                </p>
              </div>
            )}
            <p className="mt-3 text-sm text-grey-06">
              {t(`${T_PREFIX}.disable_organisation_confirm`)}
            </p>
            <div className="">
              <InputText
                type="number"
                value={confirmValue}
                onChange={(ev: any) => setConfirmValue(ev.target.value)}
                placeholder={String(nrToAnonymise)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex w-full justify-between">
              <Button variant="ghost" onClick={closeConfirm}>
                {t('forms.cancel')}
              </Button>
              <Button
                disabled={!isConfirmValid}
                onClick={onConfirm}
                variant="destruct"
              >
                {t(`${T_PREFIX}.disable_organisation`)}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default OrganisationStatusToggle;
